import * as pdfjsLib from 'pdfjs-dist';

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.js';

/**
 * PDF Knowledge Service
 * 
 * Loads and extracts text from PDF documents to create a knowledge base
 * for the VesselMind AI assistant.
 */

/**
 * Load all PDF documents and extract their text content
 * @param {Array} pdfDocuments - Array of PDF document metadata
 * @returns {Promise<Array>} Array of documents with extracted text
 */
export const loadPdfKnowledgeBase = async (pdfDocuments) => {
  console.log('🔄 Loading PDF knowledge base...');
  console.log(`📚 Total documents to process: ${pdfDocuments.length}`);
  
  const knowledgeBase = [];
  
  for (const doc of pdfDocuments) {
    try {
      console.log(`📖 Processing: ${doc.title}`);
      
      const pdfUrl = `/pdfs/codes-of-practice/${doc.filename}`;
      const textContent = await extractTextFromPdf(pdfUrl);
      
      knowledgeBase.push({
        ...doc,
        textContent,
        wordCount: textContent.split(/\s+/).length,
        loadedAt: new Date().toISOString()
      });
      
      console.log(`✅ Loaded: ${doc.title} (${textContent.split(/\s+/).length} words)`);
    } catch (error) {
      console.error(`❌ Failed to load ${doc.title}:`, error);
      knowledgeBase.push({
        ...doc,
        textContent: '',
        error: error.message,
        loadedAt: new Date().toISOString()
      });
    }
  }
  
  console.log('✅ PDF knowledge base loaded successfully!');
  console.log(`📊 Total documents: ${knowledgeBase.length}`);
  console.log(`📊 Total words: ${knowledgeBase.reduce((sum, doc) => sum + (doc.wordCount || 0), 0).toLocaleString()}`);
  
  return knowledgeBase;
};

/**
 * Extract all text from a PDF document with table structure preserved
 * @param {string} pdfUrl - URL to the PDF file
 * @returns {Promise<string>} Extracted text content
 */
export const extractTextFromPdf = async (pdfUrl) => {
  const loadingTask = pdfjsLib.getDocument(pdfUrl);
  const pdf = await loadingTask.promise;
  
  let fullText = '';
  
  // Extract text from each page
  for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
    const page = await pdf.getPage(pageNum);
    const textContent = await page.getTextContent();
    
    // Group text items by Y-coordinate (rows) to preserve table structure
    const items = textContent.items;
    const lineThreshold = 5; // Items within 5px are considered same line
    
    // Sort by Y position (top to bottom)
    items.sort((a, b) => {
      const yDiff = Math.abs(a.transform[5] - b.transform[5]);
      if (yDiff < lineThreshold) {
        // Same line - sort by X position (left to right)
        return a.transform[4] - b.transform[4];
      }
      // Different lines - sort by Y (note: PDF coords go bottom-up, so reverse)
      return b.transform[5] - a.transform[5];
    });
    
    let pageText = '';
    let lastY = null;
    let lastX = null;
    
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const currentY = item.transform[5];
      const currentX = item.transform[4];
      const text = item.str;
      
      if (lastY === null) {
        // First item
        pageText += text;
      } else {
        const yDiff = Math.abs(currentY - lastY);
        
        if (yDiff < lineThreshold) {
          // Same line - check horizontal spacing
          const xDiff = currentX - lastX;
          
          if (xDiff > 40) {
            // Large gap - likely new column (use multiple spaces or tab)
            pageText += '    ' + text;
          } else if (xDiff > 10) {
            // Medium gap - add space
            pageText += ' ' + text;
          } else if (text.match(/^[.,;:!?)}\]]/)) {
            // Punctuation - no space
            pageText += text;
          } else {
            // Small gap - single space
            pageText += ' ' + text;
          }
        } else {
          // New line
          pageText += '\n' + text;
        }
      }
      
      lastY = currentY;
      lastX = currentX + (item.width || 0);
    }
    
    fullText += pageText + '\n\n';
  }
  
  // Light cleanup - preserve structure
  return fullText
    .replace(/[ \t]+/g, ' ')  // Normalize horizontal whitespace only
    .replace(/\n{3,}/g, '\n\n')  // Max 2 newlines
    .trim();
};

/**
 * Extract smart keywords from a search query
 * Handles measurements like "32mm" by splitting into "32" and "mm"
 * @param {string} query - Search query
 * @returns {Array} Array of keywords and their variations
 */
const extractSmartKeywords = (query) => {
  const keywords = [];
  const lowerQuery = query.toLowerCase();
  
  // Extract measurement patterns (e.g., "32mm" → ["32", "mm"])
  const measurements = lowerQuery.match(/(\d+)\s*(mm|m|cm|metres?|meters?)/g) || [];
  measurements.forEach(measurement => {
    const match = measurement.match(/(\d+)\s*(mm|m|cm|metres?|meters?)/);
    if (match) {
      keywords.push(match[1]); // Just the number
      keywords.push(match[2]); // Just the unit
    }
  });
  
  // Extract material terms (common variations)
  const materials = {
    'stainless steel': ['stainless steel', 'stainless-steel', 'stainless', 'steel'],
    'carbon steel': ['carbon steel', 'carbon-steel', 'carbon', 'steel'],
    'copper': ['copper'],
  };
  
  for (const [phrase, variations] of Object.entries(materials)) {
    if (lowerQuery.includes(phrase)) {
      keywords.push(...variations);
    }
  }
  
  // Extract key technical terms
  const technicalTerms = ['support', 'span', 'distance', 'spacing', 'horizontal', 'vertical', 'pipework', 'pipe', 'separation', 'boundary', 'group', 'vessel'];
  technicalTerms.forEach(term => {
    if (lowerQuery.includes(term)) {
      keywords.push(term);
    }
  });
  
  // Vessel preparation/entry questions
  if (lowerQuery.match(/opening|open|entry|enter|preparation|prepare|before|state/i)) {
    keywords.push('opening');
    keywords.push('open');
    keywords.push('before');
    keywords.push('purge');
    keywords.push('purged');
    keywords.push('emptied');
    keywords.push('empty');
    keywords.push('preparation');
    keywords.push('entry');
  }
  
  // If query seems to be about tables/thresholds, add those terms
  if (lowerQuery.match(/how many|maximum|limit|threshold|group|at.*separation/)) {
    keywords.push('table');
    keywords.push('capacity');
    keywords.push('tonnes');
  }
  
  // Convert "how far" to "distance"
  if (lowerQuery.match(/how far|distance/)) {
    keywords.push('distance');
    keywords.push('separation');
    keywords.push('metre');
  }
  
  // Add "from" target for separation questions
  if (lowerQuery.match(/from.*\w+/)) {
    const fromMatch = lowerQuery.match(/from\s+(\w+)/);
    if (fromMatch && fromMatch[1]) {
      keywords.push(fromMatch[1]); // e.g., "from tank" → add "tank"
    }
  }
  
  // Remove duplicates
  return [...new Set(keywords)];
};

/**
 * Search for text across all PDF documents using smart keyword extraction
 * @param {Array} knowledgeBase - Array of documents with text content
 * @param {string} searchQuery - Search query
 * @param {number} maxResults - Maximum number of results to return
 * @returns {Array} Search results with context
 */
export const searchKnowledgeBase = (knowledgeBase, searchQuery, maxResults = 10) => {
  const results = [];
  const query = searchQuery.toLowerCase();
  
  // Try exact phrase match first
  const exactMatches = [];
  for (const doc of knowledgeBase) {
    if (!doc.textContent) continue;
    
    const text = doc.textContent.toLowerCase();
    let index = 0;
    
    while ((index = text.indexOf(query, index)) !== -1) {
      const contextStart = Math.max(0, index - 12000);
      const contextEnd = Math.min(text.length, index + query.length + 12000);
      const context = doc.textContent.substring(contextStart, contextEnd);
      
      exactMatches.push({
        documentId: doc.id,
        documentTitle: doc.title,
        category: doc.category,
        context: context.trim(),
        position: index,
        matchType: 'exact'
      });
      
      index += query.length;
      
      if (exactMatches.filter(r => r.documentId === doc.id).length >= 3) {
        break;
      }
    }
  }
  
  // If exact matches found, return them
  if (exactMatches.length > 0) {
    return exactMatches.slice(0, maxResults).map((result, idx) => ({
      ...result,
      rank: idx + 1
    }));
  }
  
  // No exact matches - try smart keyword search
  console.log('🔍 No exact phrase match, trying keyword search...');
  const keywords = extractSmartKeywords(searchQuery);
  console.log('📝 Extracted keywords:', keywords);
  
  if (keywords.length === 0) {
    return [];
  }
  
  // Score documents by keyword matches AND find keyword clusters
  const docScores = new Map();
  
  for (const doc of knowledgeBase) {
    if (!doc.textContent) continue;
    
    const text = doc.textContent.toLowerCase();
    const keywordMatches = new Map(); // keyword -> [positions]
    
    keywords.forEach(keyword => {
      const positions = [];
      let index = 0;
      while ((index = text.indexOf(keyword, index)) !== -1) {
        positions.push(index);
        index += keyword.length;
      }
      if (positions.length > 0) {
        keywordMatches.set(keyword, positions);
      }
    });
    
    if (keywordMatches.size === 0) continue;
    
    // Find the position where MOST keywords appear CLOSE TOGETHER (increased to 4000 for paragraph sections)
    const CLUSTER_WINDOW = 4000;
    let bestCluster = { position: 0, score: 0, keywordsFound: 0 };
    
    // Get all unique positions
    const allPositions = Array.from(keywordMatches.values()).flat().sort((a, b) => a - b);
    
    // For each position, count how many DIFFERENT keywords appear nearby
    allPositions.forEach(pos => {
      let clusterScore = 0;
      let keywordsInCluster = 0;
      
      keywordMatches.forEach((positions, keyword) => {
        // Check if this keyword appears within CLUSTER_WINDOW of pos
        const nearbyMatches = positions.filter(p => 
          Math.abs(p - pos) <= CLUSTER_WINDOW
        );
        if (nearbyMatches.length > 0) {
          keywordsInCluster++;
          clusterScore += nearbyMatches.length;
        }
      });
      
      // Prefer clusters with more DIFFERENT keywords
      if (keywordsInCluster > bestCluster.keywordsFound || 
         (keywordsInCluster === bestCluster.keywordsFound && clusterScore > bestCluster.score)) {
        bestCluster = {
          position: pos,
          score: clusterScore,
          keywordsFound: keywordsInCluster
        };
      }
    });
    
    console.log(`📍 Best cluster in ${doc.title}: position ${bestCluster.position}, ${bestCluster.keywordsFound}/${keywords.length} keywords, score ${bestCluster.score}`);
    
    if (bestCluster.keywordsFound > 0) {
      docScores.set(doc.id, {
        doc,
        score: bestCluster.score,
        bestPosition: bestCluster.position,
        keywordsFound: bestCluster.keywordsFound
      });
    }
  }
  
  // Get top documents (prioritize by keywords found, then by score)
  const topDocs = Array.from(docScores.values())
    .sort((a, b) => {
      if (b.keywordsFound !== a.keywordsFound) {
        return b.keywordsFound - a.keywordsFound; // More keywords = better
      }
      return b.score - a.score; // Same keywords = higher score wins
    })
    .slice(0, maxResults);
  
  // Extract context from best positions (increased to 12000 for complete sections with all conditions)
  topDocs.forEach(({ doc, bestPosition }) => {
    const contextStart = Math.max(0, bestPosition - 12000);
    const contextEnd = Math.min(doc.textContent.length, bestPosition + 12000);
    const context = doc.textContent.substring(contextStart, contextEnd);
    
    results.push({
      documentId: doc.id,
      documentTitle: doc.title,
      category: doc.category,
      context: context.trim(),
      position: bestPosition,
      matchType: 'keywords'
    });
  });
  
  return results.map((result, idx) => ({
    ...result,
    rank: idx + 1
  }));
};

/**
 * Get relevant document sections based on a query
 * @param {Array} knowledgeBase - Array of documents with text content
 * @param {string} query - User query
 * @returns {Array} Relevant document sections
 */
export const getRelevantSections = (knowledgeBase, query) => {
  const keywords = query
    .toLowerCase()
    .split(/\s+/)
    .filter(word => word.length > 3);  // Filter short words
  
  const scoredDocs = knowledgeBase.map(doc => {
    if (!doc.textContent) return { ...doc, relevanceScore: 0, bestSection: '' };
    
    const text = doc.textContent.toLowerCase();
    let score = 0;
    
    // Score based on keyword matches
    keywords.forEach(keyword => {
      const regex = new RegExp(keyword, 'gi');
      const matches = text.match(regex);
      if (matches) {
        score += matches.length;
      }
    });
    
    // Bonus for title matches
    if (doc.title.toLowerCase().includes(query.toLowerCase())) {
      score += 10;
    }
    
    // Find the best section (most keyword-dense area)
    let bestSection = '';
    if (score > 0) {
      bestSection = findBestSection(doc.textContent, keywords);
    }
    
    return { ...doc, relevanceScore: score, bestSection };
  });
  
  // Return top scoring documents
  return scoredDocs
    .filter(doc => doc.relevanceScore > 0)
    .sort((a, b) => b.relevanceScore - a.relevanceScore)
    .slice(0, 5);
};

/**
 * Find the most relevant section of a document for given keywords
 * @param {string} text - Full document text
 * @param {Array} keywords - Keywords to search for
 * @returns {string} Best section (up to 5000 chars)
 */
const findBestSection = (text, keywords) => {
  const CHUNK_SIZE = 5000; // Increased from 2500 for better table coverage
  const OVERLAP = 1000; // Increased overlap for tables
  const chunks = [];
  
  // Split document into overlapping chunks
  for (let i = 0; i < text.length; i += (CHUNK_SIZE - OVERLAP)) {
    chunks.push({
      text: text.substring(i, i + CHUNK_SIZE),
      start: i
    });
  }
  
  // Score each chunk by keyword density
  const scoredChunks = chunks.map(chunk => {
    const chunkLower = chunk.text.toLowerCase();
    let score = 0;
    
    keywords.forEach(keyword => {
      const regex = new RegExp(keyword, 'gi');
      const matches = chunkLower.match(regex);
      if (matches) {
        score += matches.length;
      }
    });
    
    // Bonus for chunks containing table indicators
    if (chunkLower.includes('table') || chunkLower.includes('column')) {
      score += 5;
    }
    
    return { ...chunk, score };
  });
  
  // Sort by score and get top 3 chunks (increased from 2 for better table coverage)
  scoredChunks.sort((a, b) => b.score - a.score);
  const topChunks = scoredChunks.slice(0, 3);
  
  // Combine top chunks
  if (topChunks.length === 1) {
    return topChunks[0].text;
  } else if (topChunks.length >= 2) {
    return topChunks.map(c => c.text).join('\n...\n');
  }
  
  return text.substring(0, 10000); // Increased fallback from 5000
};

